import React from 'react';
import PropTypes from 'prop-types';
import { FaTimes } from 'react-icons/fa';
import { INPUT_CLS, TEXTAREA_CLS, INPUT_ERROR_CLS } from 'helpers/formStyles';

/**
 * FormInput component with label, error state, and optional clear button
 * Supports text, email, number, date, time, textarea
 */
const FormInput = React.forwardRef(({
  label,
  type = 'text',
  value,
  onChange,
  onClear,
  placeholder,
  error,
  disabled = false,
  required = false,
  rows,
  className = '',
  ...props
}, ref) => {
  const isTextarea = type === 'textarea';
  const baseClasses = isTextarea ? TEXTAREA_CLS : INPUT_CLS;
  const errorClass = error ? INPUT_ERROR_CLS : '';

  const Component = isTextarea ? 'textarea' : 'input';

  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      {label && (
        <label className="tw-text-sm tw-font-medium tw-text-slate-700">
          {label}
          {required && <span className="tw-text-red-500 tw-ml-1">*</span>}
        </label>
      )}

      <div className="tw-relative tw-flex tw-items-center">
        <Component
          ref={ref}
          type={isTextarea ? undefined : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={`${baseClasses} ${errorClass} ${className}`}
          {...props}
        />
        {onClear && value && !disabled && (
          <button
            onClick={onClear}
            className="tw-absolute tw-right-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-1"
            type="button"
            aria-label="Clear input"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {error && (
        <p className="tw-text-xs tw-text-red-600 tw-font-medium">{error}</p>
      )}
    </div>
  );
});

FormInput.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'email', 'number', 'date', 'time', 'textarea', 'password', 'tel']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  rows: PropTypes.number,
  className: PropTypes.string,
};

export default FormInput;
